import {
  findIconDefinition,
  library,
  type IconName,
  type IconPrefix,
} from "@fortawesome/fontawesome-svg-core";
import {
  faCircleQuestion,
  fas,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  forwardRef,
  type CSSProperties,
  type ForwardRefExoticComponent,
  type RefAttributes,
  type SVGProps,
} from "react";

library.add(fas);

type FontAwesomeCompatibleProps = Omit<SVGProps<SVGSVGElement>, "ref"> & {
  absoluteStrokeWidth?: boolean;
  size?: number | string;
};

export type LucideIcon = ForwardRefExoticComponent<
  FontAwesomeCompatibleProps & RefAttributes<SVGSVGElement>
>;

function createFontAwesomeIcon(
  iconName: IconName,
  prefix: IconPrefix = "fas",
): LucideIcon {
  const Component = forwardRef<SVGSVGElement, FontAwesomeCompatibleProps>(
    (
      {
        absoluteStrokeWidth: _absoluteStrokeWidth,
        fill: _fill,
        size,
        stroke: _stroke,
        strokeLinecap: _strokeLinecap,
        strokeLinejoin: _strokeLinejoin,
        strokeWidth: _strokeWidth,
        style,
        ...props
      },
      ref,
    ) => {
      const icon =
        findIconDefinition({ prefix, iconName }) ?? faCircleQuestion;
      const dimension =
        typeof size === "number" ? `${size}px` : size;
      const iconStyle: CSSProperties = {
        ...(style as CSSProperties),
        ...(dimension ? { width: dimension, height: dimension } : {}),
      };

      return (
        <FontAwesomeIcon
          {...props}
          ref={ref}
          icon={icon}
          style={iconStyle}
        />
      );
    },
  );

  Component.displayName = `FontAwesomeIcon(${iconName})`;
  return Component;
}

export const Activity = createFontAwesomeIcon("chart-line");
export const AlertCircle = createFontAwesomeIcon("circle-exclamation");
export const AlertTriangle = createFontAwesomeIcon("triangle-exclamation");
export const ArrowDownToLine = createFontAwesomeIcon("download");
export const ArrowLeft = createFontAwesomeIcon("arrow-left");
export const ArrowLeftRight = createFontAwesomeIcon("right-left");
export const ArrowRight = createFontAwesomeIcon("arrow-right");
export const ArrowRightLeft = createFontAwesomeIcon("right-left");
export const ArrowUpFromLine = createFontAwesomeIcon("upload");
export const BadgeCheck = createFontAwesomeIcon("certificate");
export const BadgeDollarSign = createFontAwesomeIcon("dollar-sign");
export const BadgeInfo = createFontAwesomeIcon("circle-info");
export const Ban = createFontAwesomeIcon("ban");
export const Banknote = createFontAwesomeIcon("money-bill");
export const BarChart3 = createFontAwesomeIcon("chart-column");
export const Bell = createFontAwesomeIcon("bell");
export const BellRing = createFontAwesomeIcon("bell");
export const Bot = createFontAwesomeIcon("robot");
export const Boxes = createFontAwesomeIcon("boxes-stacked");
export const BriefcaseBusiness = createFontAwesomeIcon("briefcase");
export const Calculator = createFontAwesomeIcon("calculator");
export const Camera = createFontAwesomeIcon("camera");
export const CarFront = createFontAwesomeIcon("car");
export const Check = createFontAwesomeIcon("check");
export const CheckCircle = createFontAwesomeIcon("circle-check");
export const CheckCircle2 = CheckCircle;
export const CheckIcon = Check;
export const ChevronDown = createFontAwesomeIcon("chevron-down");
export const ChevronDownIcon = ChevronDown;
export const ChevronLeft = createFontAwesomeIcon("chevron-left");
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRight = createFontAwesomeIcon("chevron-right");
export const ChevronRightIcon = ChevronRight;
export const ChevronUp = createFontAwesomeIcon("chevron-up");
export const ChevronUpIcon = ChevronUp;
export const Circle = createFontAwesomeIcon("circle");
export const CircleDollarSign = createFontAwesomeIcon("dollar-sign");
export const CircleHelp = createFontAwesomeIcon("circle-question");
export const CircleIcon = Circle;
export const ClipboardList = createFontAwesomeIcon("clipboard-list");
export const Clock = createFontAwesomeIcon("clock");
export const Clock3 = Clock;
export const Cog = createFontAwesomeIcon("gear");
export const CreditCard = createFontAwesomeIcon("credit-card");
export const Crown = createFontAwesomeIcon("crown");
export const Disc = createFontAwesomeIcon("compact-disc");
export const DollarSign = createFontAwesomeIcon("dollar-sign");
export const Download = createFontAwesomeIcon("download");
export const Edit = createFontAwesomeIcon("pen-to-square");
export const Eraser = createFontAwesomeIcon("eraser");
export const ExternalLink = createFontAwesomeIcon("arrow-up-right-from-square");
export const Eye = createFontAwesomeIcon("eye");
export const EyeOff = createFontAwesomeIcon("eye-slash");
export const FileBarChart = createFontAwesomeIcon("chart-bar");
export const FileCheck2 = createFontAwesomeIcon("file-circle-check");
export const FileImage = createFontAwesomeIcon("file-image");
export const FileSpreadsheet = createFontAwesomeIcon("file-excel");
export const Film = createFontAwesomeIcon("film");
export const FlaskConical = createFontAwesomeIcon("flask");
export const GitBranch = createFontAwesomeIcon("code-branch");
export const GripVerticalIcon = createFontAwesomeIcon("grip-vertical");
export const Hash = createFontAwesomeIcon("hashtag");
export const Heart = createFontAwesomeIcon("heart");
export const History = createFontAwesomeIcon("clock-rotate-left");
export const Home = createFontAwesomeIcon("house");
export const ImageIcon = createFontAwesomeIcon("image");
export const ImagePlus = createFontAwesomeIcon("image");
export const Images = createFontAwesomeIcon("images");
export const Info = createFontAwesomeIcon("circle-info");
export const KeyRound = createFontAwesomeIcon("key");
export const Landmark = createFontAwesomeIcon("building-columns");
export const LayoutDashboard = createFontAwesomeIcon("gauge-high");
export const Lightbulb = createFontAwesomeIcon("lightbulb");
export const Loader2 = createFontAwesomeIcon("spinner");
export const LoaderCircle = Loader2;
export const LocateFixed = createFontAwesomeIcon("location-crosshairs");
export const LogIn = createFontAwesomeIcon("right-to-bracket");
export const LogOut = createFontAwesomeIcon("right-from-bracket");
export const Mail = createFontAwesomeIcon("envelope");
export const MailCheck = createFontAwesomeIcon("envelope-circle-check");
export const MapPin = createFontAwesomeIcon("location-dot");
export const Maximize2 = createFontAwesomeIcon("expand");
export const Megaphone = createFontAwesomeIcon("bullhorn");
export const Menu = createFontAwesomeIcon("bars");
export const MessageCircle = createFontAwesomeIcon("comment");
export const MessageCircleQuestion = createFontAwesomeIcon("circle-question");
export const MessageSquare = createFontAwesomeIcon("comment");
export const Minus = createFontAwesomeIcon("minus");
export const MinusIcon = Minus;
export const Moon = createFontAwesomeIcon("moon");
export const MoreHorizontal = createFontAwesomeIcon("ellipsis");
export const MoreHorizontalIcon = MoreHorizontal;
export const MoreVertical = createFontAwesomeIcon("ellipsis-vertical");
export const MousePointerClick = createFontAwesomeIcon("arrow-pointer");
export const Navigation = createFontAwesomeIcon("location-arrow");
export const Package = createFontAwesomeIcon("box");
export const PackageCheck = createFontAwesomeIcon("box");
export const Paperclip = createFontAwesomeIcon("paperclip");
export const PackagePlus = createFontAwesomeIcon("box");
export const PackageX = createFontAwesomeIcon("box-open");
export const PanelLeftIcon = createFontAwesomeIcon("table-columns");
export const Pause = createFontAwesomeIcon("pause");
export const Pencil = createFontAwesomeIcon("pen");
export const Percent = createFontAwesomeIcon("percent");
export const Phone = createFontAwesomeIcon("phone");
export const Play = createFontAwesomeIcon("play");
export const Plus = createFontAwesomeIcon("plus");
export const QrCode = createFontAwesomeIcon("qrcode");
export const ReceiptText = createFontAwesomeIcon("receipt");
export const RefreshCcw = createFontAwesomeIcon("arrows-rotate");
export const RefreshCw = RefreshCcw;
export const Reply = createFontAwesomeIcon("reply");
export const RotateCcw = createFontAwesomeIcon("rotate-left");
export const Route = createFontAwesomeIcon("route");
export const Save = createFontAwesomeIcon("floppy-disk");
export const ScanLine = createFontAwesomeIcon("qrcode");
export const Search = createFontAwesomeIcon("magnifying-glass");
export const SearchIcon = Search;
export const Send = createFontAwesomeIcon("paper-plane");
export const Settings = createFontAwesomeIcon("gears");
export const Shield = createFontAwesomeIcon("shield");
export const ShieldAlert = createFontAwesomeIcon("shield-halved");
export const ShieldCheck = createFontAwesomeIcon("shield-halved");
export const ShoppingBag = createFontAwesomeIcon("bag-shopping");
export const ShoppingCart = createFontAwesomeIcon("cart-shopping");
export const SlidersHorizontal = createFontAwesomeIcon("sliders");
export const Sparkles = createFontAwesomeIcon("wand-magic-sparkles");
export const Star = createFontAwesomeIcon("star");
export const Store = createFontAwesomeIcon("store");
export const Sun = createFontAwesomeIcon("sun");
export const Target = createFontAwesomeIcon("bullseye");
export const Trash2 = createFontAwesomeIcon("trash-can");
export const TrendingUp = createFontAwesomeIcon("arrow-trend-up");
export const Truck = createFontAwesomeIcon("truck");
export const Undo2 = createFontAwesomeIcon("rotate-left");
export const Upload = createFontAwesomeIcon("upload");
export const User = createFontAwesomeIcon("user");
export const UserRound = User;
export const UserRoundSearch = User;
export const Users = createFontAwesomeIcon("user-group");
export const WalletCards = createFontAwesomeIcon("wallet");
export const Warehouse = createFontAwesomeIcon("warehouse");
export const Wind = createFontAwesomeIcon("wind");
export const Wrench = createFontAwesomeIcon("wrench");
export const X = createFontAwesomeIcon("xmark");
export const XCircle = createFontAwesomeIcon("circle-xmark");
export const XIcon = X;
export const Zap = createFontAwesomeIcon("bolt");
